import { INotification } from './typings';

type NotificationTitlePart = {
  text: string;
  emphasize: boolean;
};

export type NotificationTitle = {
  avatarName: string;
  parts: NotificationTitlePart[];
};

const ACTION_LED_SUFFIX_PATTERN =
  /\b(replied|commented|tagged|mentioned|liked|reacted|sent|added|posted|shared|joined|followed)\b/i;

const LEADING_FROM_PATTERN = /^from\s+/i;

const normalizeText = (value?: string | null) => value?.trim().replace(/\s+/g, ' ') ?? '';

const matchesWholeValue = (left: string, right: string) =>
  Boolean(left && right) && left.toLowerCase() === right.toLowerCase();

const stripTags = (value: string) => value.replace(/<\/?[a-zA-Z][^>]*>/g, '');

const cleanText = (value?: string | null) =>
  normalizeText(stripTags(value ?? '').replace(/&nbsp;/g, ' '));

// Server label shape: `<prefix> - <description>` (openspec notifications, route chip).
const ROUTE_LABEL_SEPARATOR = ' - ';

const containsValue = (value: string, search: string) =>
  Boolean(value && search) && value.toLowerCase().includes(search.toLowerCase());

const stripBoundaryDuplicate = (text: string, duplicate: string) => {
  if (!text || !duplicate) return text;

  if (matchesWholeValue(text, duplicate)) {
    return '';
  }

  const lowerText = text.toLowerCase();
  const lowerDuplicate = duplicate.toLowerCase();

  if (lowerText.startsWith(`${lowerDuplicate} `)) {
    return text.slice(duplicate.length).trim();
  }

  if (lowerText.endsWith(` ${lowerDuplicate}`)) {
    return text.slice(0, text.length - duplicate.length).trim();
  }

  return text;
};

const stripTrailingDuplicate = (text: string, duplicate: string) => {
  if (!text || !duplicate) return text;

  const lowerText = text.toLowerCase();
  const lowerDuplicate = duplicate.toLowerCase();

  if (lowerText.endsWith(` ${lowerDuplicate}`)) {
    return text.slice(0, text.length - duplicate.length).trim();
  }

  return text;
};

const getChatMessageTitle = (notification: INotification): NotificationTitle | null => {
  const misc = notification.miscellaneous;
  if (misc?.createdFrom !== 'conversation') return null;

  const senderName = normalizeText(misc.senderName);
  if (!senderName) return null;

  const parts: NotificationTitlePart[] = [
    { text: 'New message from', emphasize: false },
    { text: senderName, emphasize: true },
  ];

  const conversationName = normalizeText(misc.conversationName ?? misc.routeData?.conversationName);
  if (misc.conversationType === 'group_chat' && conversationName) {
    parts.push({ text: `in ${conversationName}`, emphasize: false });
  }

  return { avatarName: senderName, parts };
};

export const getNotificationTitle = (notification: INotification): NotificationTitle => {
  const chatMessageTitle = getChatMessageTitle(notification);
  if (chatMessageTitle) return chatMessageTitle;

  const rawHeading = normalizeText(notification.name);
  const actor = normalizeText(notification.miscellaneous?.mentionedBy?.userName);
  const routeLabel = normalizeText(notification.miscellaneous?.routeLabel);
  const rawSuffix = normalizeText(notification.miscellaneous?.suffixLabel);
  const suffix = matchesWholeValue(rawSuffix, routeLabel) ? '' : rawSuffix;
  const conversationName = normalizeText(notification.miscellaneous?.routeData?.conversationName);
  const actorFromSuffix = suffix.toLowerCase().startsWith('from ') ? suffix.slice(5).trim() : '';
  const heading =
    actor && ACTION_LED_SUFFIX_PATTERN.test(rawHeading)
      ? stripTrailingDuplicate(rawHeading, actor)
      : rawHeading;

  const parts: NotificationTitlePart[] = [];
  const actorLeadsTitle =
    Boolean(actor) &&
    (!heading ||
      (ACTION_LED_SUFFIX_PATTERN.test(suffix) &&
        !matchesWholeValue(actor, heading) &&
        !containsValue(heading, actor)));

  if (actorLeadsTitle) {
    parts.push({ text: actor, emphasize: true });

    const actorSuffix = matchesWholeValue(suffix, `from ${actor}`)
      ? ''
      : stripBoundaryDuplicate(suffix, actor);
    if (actorSuffix) {
      parts.push({ text: actorSuffix, emphasize: false });
    }
  } else {
    if (heading) {
      parts.push({ text: heading, emphasize: true });
    }

    const headingAlreadyIncludesSuffixActor =
      actorFromSuffix &&
      containsValue(heading, actorFromSuffix) &&
      matchesWholeValue(suffix, `from ${actorFromSuffix}`);
    const headingSuffix = headingAlreadyIncludesSuffixActor
      ? ''
      : stripBoundaryDuplicate(suffix, heading);

    const showsActor =
      Boolean(actor) &&
      !matchesWholeValue(actor, heading) &&
      !containsValue(heading, actor) &&
      !containsValue(headingSuffix, actor) &&
      !matchesWholeValue(headingSuffix, `from ${actor}`);

    // The suffix names a place ("from Group Union 1") while the actor names a
    // person. Printed side by side they ran together — "You got a new message
    // Demo Co. from Group Union 1" reads as if the message came from the group.
    // Give the actor its own preposition and demote the suffix's to "in", so the
    // two nouns stay apart.
    const suffixNamesAPlace = showsActor && LEADING_FROM_PATTERN.test(headingSuffix);

    if (showsActor) {
      if (suffixNamesAPlace) {
        parts.push({ text: 'from', emphasize: false });
      }
      parts.push({ text: actor, emphasize: true });
    }

    if (headingSuffix) {
      parts.push({
        text: suffixNamesAPlace
          ? headingSuffix.replace(LEADING_FROM_PATTERN, 'in ')
          : headingSuffix,
        emphasize: false,
      });
    }
  }

  if (conversationName) {
    parts.forEach((part) => {
      part.text = part.text.replace(/\bin your crew\b/i, `in ${conversationName}`);
    });
  }

  const renderedTitle = parts.map((part) => part.text).join(' ');
  if (conversationName && !containsValue(renderedTitle, conversationName)) {
    parts.push({ text: `in ${conversationName}`, emphasize: false });
  }

  return {
    avatarName: actor || actorFromSuffix || heading,
    parts,
  };
};

/**
 * The secondary line under a tray row: what the notification is actually about.
 *
 * A chat row's `description` holds the conversation name, which the title
 * already says, so chat rows read `messagePreview` instead. Everything else
 * reads `description`, which is where the server writes a mention's comment
 * text. Returns '' when there is nothing to add or it would repeat the title.
 */
export const getNotificationPreview = (notification: INotification): string => {
  const misc = notification.miscellaneous;
  const raw =
    misc?.createdFrom === 'conversation' ? misc?.messagePreview : notification.description;

  const preview = cleanText(typeof raw === 'string' ? raw : '');
  if (!preview) return '';

  const renderedTitle = getNotificationTitle(notification)
    .parts.map((part) => part.text)
    .join(' ');
  if (matchesWholeValue(preview, renderedTitle)) return '';

  if (matchesWholeValue(preview, cleanText(misc?.routeLabel))) return '';

  return preview;
};

/**
 * The route chip's text: the label's prefix alone when the preview line already
 * shows the text after it.
 */
export const getNotificationRouteLabel = (notification: INotification): string => {
  const rawLabel = notification.miscellaneous?.routeLabel ?? '';
  const at = rawLabel.indexOf(ROUTE_LABEL_SEPARATOR);
  if (at < 0) return cleanText(rawLabel);

  const prefix = cleanText(rawLabel.slice(0, at));
  const body = cleanText(rawLabel.slice(at + ROUTE_LABEL_SEPARATOR.length));
  if (!body || matchesWholeValue(body, getNotificationPreview(notification))) return prefix;

  return cleanText(rawLabel);
};
